// Pause dîner (Paramètres système) au Gantt : un bloc « Dîner » à part, comme l'Inspection.
// Comme le serveur, le bloc suit la fin de l'arrêt qui porte la pause, avant son trajet. Il ne coupe jamais un arrêt :
// le trajet de cet arrêt reprend après lui, et ce qui empiète encore (intervalle) s'arrête avant ou reprend après.
// La pause n'est pas du temps travaillé.
const LUNCH_BREAK_BUFFER_NOTE = "Pause dîner";

// Segment du Gantt (manualBuffer…) ou segment de route du serveur (manual_buffer_…).
export function isLunchBreakSegment(segment) {
  return (segment?.manualBufferNote ?? segment?.manual_buffer_note) === LUNCH_BREAK_BUFFER_NOTE
    && (segment?.manualBufferUpdatedBy ?? segment?.manual_buffer_updated_by) === "Paramètres système"
    && Number(segment?.manualBufferMinutes ?? segment?.manual_buffer_minutes) > 0;
}

// « 12:30 » ou « 2026-10-13T12:30 » → minutes depuis minuit.
export function lunchClockMinutes(value) {
  const match = /(?:^|T)(\d{1,2}):(\d{2})/.exec(typeof value === "string" ? value.trim() : "");
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function itemSource(item) {
  return item?.kind === "segment" ? item.segment : item;
}

function findLunchBreakSegment(source) {
  return [source, ...(Array.isArray(source?.memberSegments) ? source.memberSegments : [])].find(isLunchBreakSegment) || null;
}

function stepKeys(step) {
  return step?.step_type === "loading"
    ? [`loading:${step.order_id}`]
    : [`delivery:${step?.order_id}:${step?.delivery_id}`, `delivery:${step?.order_id}`];
}

// Fin de l'arrêt qui porte la pause et arrivée à l'arrêt suivant (heures affichées, pause comprise).
function resolveStopClock(assignment, stepKey) {
  const steps = Array.isArray(assignment?.operational_steps) ? assignment.operational_steps : [];
  const index = steps.findIndex((step) => stepKeys(step).includes(stepKey));
  const departure = lunchClockMinutes(steps[index]?.display_departure_at || steps[index]?.departure_at);
  const nextArrival = index < 0 || departure == null ? null : steps.slice(index + 1)
    .map((step) => lunchClockMinutes(step?.display_arrival_at || step?.arrival_at))
    .find((minutes) => minutes != null && minutes > departure + 0.5);
  return { departure, nextArrival: nextArrival ?? null };
}

// Heures de la pause (minutes depuis minuit) : bloc du Gantt et ligne « Dîner » de la Séquence route.
export function resolveLunchBreakWindow(assignment, segment) {
  const { departure, nextArrival } = resolveStopClock(assignment, segment?.manualBufferStepKey);
  if (!isLunchBreakSegment(segment) || departure == null) {
    return null;
  }

  const minutes = Math.round(Number(segment.manualBufferMinutes));
  return {
    afterLastStop: nextArrival == null,
    endMinutes: departure + minutes,
    lunchMinutes: lunchClockMinutes(assignment?.lunch_time),
    minutes,
    nextArrivalMinutes: nextArrival,
    startMinutes: departure,
  };
}

const EDGE = 0.05;

// Mêmes marges et même échelle que les segments (buildSegmentPositionData).
export function resolveLunchBreakTimeline(items, assignment, {
  leadingPaddingPercent = 0,
  scaleMinutes = 0,
  timelineStartMinutes = 0,
  trailingPaddingPercent = 0,
} = {}) {
  const renderItems = Array.isArray(items) ? items : [];
  const leading = Math.min(Math.max(Number(leadingPaddingPercent) || 0, 0), 40);
  const usablePercent = 100 - leading - Math.min(Math.max(Number(trailingPaddingPercent) || 0, 0), 40);
  const scale = Number(scaleMinutes);
  const carrierItem = scale > 0 && usablePercent > 0
    ? renderItems.find((item) => item?.kind !== "idle" && findLunchBreakSegment(itemSource(item)))
    : null;
  const lunchBreak = resolveLunchBreakWindow(assignment, carrierItem ? findLunchBreakSegment(itemSource(carrierItem)) : null);
  if (!lunchBreak) {
    return { block: null, items: renderItems };
  }

  const toPercent = (minutes) => leading + ((minutes - Number(timelineStartMinutes)) / scale) * usablePercent;
  const { afterLastStop } = lunchBreak;
  const leftPercent = toPercent(lunchBreak.startMinutes);
  const rightPercent = toPercent(lunchBreak.endMinutes);
  const carrier = itemSource(carrierItem);
  const carrierEnd = (Number(carrier.leftPercent) || 0) + Math.max(Number(carrier.widthPercent) || 0, 0);
  // Fin du trajet de l'arrêt porteur. Un arrêt ancré (P1, P2) est dessiné sans sa pause : son trajet est prolongé
  // d'autant, sans dépasser l'arrivée à l'arrêt suivant.
  const routeEnd = afterLastStop
    ? carrierEnd
    : Math.max(carrierEnd, Math.min(carrierEnd + rightPercent - leftPercent, toPercent(lunchBreak.nextArrivalMinutes)));
  const zoneEnd = Math.max(rightPercent, routeEnd);
  const cut = (item) => {
    const source = itemSource(item);
    if (!source || source.isInitialPreload || source.isEndOfDayLoad || source.isEndOfDayLoadSourcePreload) {
      return item;
    }
    const left = Number(source.leftPercent) || 0;
    const width = Math.max(Number(source.widthPercent) || 0, 0);
    let next = null;
    if (item === carrierItem && !afterLastStop) {
      // Arrêt porteur : il garde son trajet, que le bloc recouvre à la sortie du service ; son texte s'arrête avant le bloc
      // (marge droite, en % de la piste).
      next = { leftPercent: left, lunchBreakInsetPercent: routeEnd - leftPercent, widthPercent: routeEnd - left };
    } else if (left + width > leftPercent + EDGE && left < zoneEnd - EDGE) {
      // Empiète sur le bloc ou sur le trajet qui le suit : s'arrête avant le bloc, sinon reprend après.
      next = left < leftPercent - EDGE
        ? { leftPercent: left, widthPercent: leftPercent - left }
        : { leftPercent: zoneEnd, widthPercent: Math.max(left + width - zoneEnd, 0) };
    } else if (afterLastStop && Math.abs(left + width - leftPercent) <= EDGE && findLunchBreakSegment(source)) {
      // Dernier arrêt (retour non dessiné) : l'élément n'est que l'arrêt, tout en service.
      next = { servicePercent: 100 };
    }
    if (!next) {
      return item;
    }
    // La part service garde sa largeur.
    if (next.widthPercent !== undefined && Number(source.servicePercent) > 0 && next.widthPercent > 0) {
      next.servicePercent = Math.min((Number(source.servicePercent) * width) / next.widthPercent, 100);
    }
    return item.kind === "segment" ? { ...item, segment: { ...source, ...next } } : { ...source, ...next };
  };

  return {
    block: { ...lunchBreak, leftPercent, widthPercent: rightPercent - leftPercent },
    items: renderItems.map(cut),
  };
}
